// src/services/reportsService.js
// Unified Reports Service

const API_URL = process.env.REACT_APP_API_URL || 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

// Helper to get auth token from MSAL (same pattern as api.js)
async function getAuthToken() {
  try {
    const msalInstance = window.msalInstance;
    if (!msalInstance) {
      console.log('[ReportsService] MSAL not initialized');
      return null;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.log('[ReportsService] No authenticated accounts');
      return null;
    }

    const tokenRequest = {
      scopes: ['openid', 'profile', 'email'],
      account: accounts[0],
    };

    const response = await msalInstance.acquireTokenSilent(tokenRequest);
    return response.idToken;
  } catch (error) {
    console.log('[ReportsService] Failed to acquire token:', error.message);
    return null;
  }
}

/**
 * List all reports (scans, validate jobs, trends)
 * @param {Object} options - Filter options
 * @param {string} options.type - Report type: 'all', 'scan', 'validate', 'trend'
 * @param {string} options.status - Status filter: 'complete', 'running', 'failed'
 * @param {number} options.limit - Max results to return
 */
export async function listReports(options = {}) {
  const token = await getAuthToken();

  const params = new URLSearchParams();
  if (options.type && options.type !== 'all') params.append('type', options.type);
  if (options.status && options.status !== 'all') params.append('status', options.status);
  if (options.limit) params.append('limit', options.limit.toString());

  const queryString = params.toString();
  const url = `${API_URL}/reports-list${queryString ? '?' + queryString : ''}`;

  console.log('[ReportsService] Fetching reports:', url);

  const headers = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch reports: ${response.status}`);
  }

  const data = await response.json();
  console.log('[ReportsService] Received:', data.count, 'reports');
  return data;
}

/**
 * Get view URL for a report based on its type
 */
const ALLOWED_VIEW_URL_PREFIXES = [
  '/scan-result/',
  '/scrutinize-result/',
  '/validate/results/',
  '/weekly-report/',
];

export function getReportViewUrl(report) {
  // PRD-75 Session 2 (2026-05-24): prefer the backend-built `viewUrl` over
  // re-deriving from `report.id` because trend rowKeys (e.g. `weekly_2026-W22`)
  // collide across assets — the fallback table lookup in /weekly-report-get
  // picks an arbitrary blob and may 404/403. Prefix guard (2026-05-30) catches
  // a backend that emits an unrouted path and falls back to the switch instead
  // of silently 404'ing in the browser.
  if (report.viewUrl) {
    if (ALLOWED_VIEW_URL_PREFIXES.some((p) => report.viewUrl.startsWith(p))) {
      return report.viewUrl;
    }
    console.warn('[reportsService] Ignoring backend viewUrl with unknown prefix:', report.viewUrl);
  }

  switch (report.reportType) {
    case 'scan':
      if (report.scanType === 'scrutinize' || report.type === 'scrutinize') {
        return `/scrutinize-result/${report.id}`;
      } else {
        return `/scan-result/${report.id}`;
      }
    case 'validate':
      return `/validate/results/${report.id}`;
    case 'trend':
      return `/weekly-report/${report.id}`;
    default:
      return `/scan-result/${report.id}`;
  }
}

/**
 * Get a weekly trend report by ID from blob storage
 * @param {string} reportId - Report ID (format: report-{assetId}-{weekNumber})
 */
export async function getWeeklyReport(reportId) {
  const token = await getAuthToken();

  const url = `${API_URL}/weekly-report-get?reportId=${encodeURIComponent(reportId)}`;
  console.log('[ReportsService] Fetching weekly report:', url);

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch weekly report: ${response.status}`);
  }

  return response.json();
}

/**
 * Get account-level weekly report
 * @param {string} weekNumber - Week in YYYY-WNN format (optional, defaults to current)
 */
export async function getAccountWeeklyReport(weekNumber = null) {
  const token = await getAuthToken();

  let url = `${API_URL}/account-weekly-report-get`;
  if (weekNumber) {
    url += `?week=${encodeURIComponent(weekNumber)}`;
  }

  console.log('[ReportsService] Fetching account weekly report:', url);

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch account report: ${response.status}`);
  }

  return response.json();
}

/**
 * Get display label for report type
 */
export function getReportTypeLabel(report) {
  switch (report.reportType) {
    case 'scan':
      return report.scanType === 'scrutinize' ? 'DEEP SCAN' : 'Fast Scan';
    case 'validate':
      return 'VALIDATE';
    case 'trend':
      return report.periodType === 'monthly' ? 'Monthly Trend' : 'Weekly Trend';
    default:
      return 'Report';
  }
}

/**
 * Get icon/emoji for report type
 */
export function getReportTypeIcon(report) {
  switch (report.reportType) {
    case 'scan':
      return report.scanType === 'scrutinize' ? '🔍' : '⚡';
    case 'validate':
      return '✓';
    case 'trend':
      return '📊';
    default:
      return '📄';
  }
}

/**
 * Get trend data for charts (risk scores, vuln counts, drift activity)
 * @param {string} assetId - Asset ID (or 'all' for aggregate)
 * @param {number} weeks - Number of weeks of data (default 12)
 */
export async function getTrendData(assetId = null, weeks = 12) {
  const token = await getAuthToken();

  let url = `${API_URL}/trend-data?weeks=${weeks}`;
  if (assetId && assetId !== 'all') {
    url += `&assetId=${encodeURIComponent(assetId)}`;
  }

  console.log('[ReportsService] Fetching trend data:', url);

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch trend data: ${response.status}`);
  }

  return response.json();
}

/**
 * Get user's assets for the asset selector
 */
export async function getUserAssets() {
  const token = await getAuthToken();

  const url = `${API_URL}/assets-list`;
  console.log('[ReportsService] Fetching user assets:', url);

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch assets: ${response.status}`);
  }

  return response.json();
}

const reportsService = {
  listReports,
  getReportViewUrl,
  getReportTypeLabel,
  getReportTypeIcon,
  getWeeklyReport,
  getAccountWeeklyReport,
  getTrendData,
  getUserAssets
};

export default reportsService;
